"use client";

import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";

import HorizontalArticleCard from "@/components/cards/HorizontalArticleCard";
import { getLabel } from "@/data/labels";
import { LATESTNEWS_RESULT_NEWS_LIMIT } from "@/data/constants";
import { fetchLatestNews } from "@/services/newsService";
import { ArticleType } from "@/types/article";

interface NewsListSectionProps {
  initialData: {
    docs: ArticleType[];
    totalDocs: number;
    limit: number;
    page: number;
  };
  lang: AppLocale;
}

export default function NewsListSection({ initialData, lang }: NewsListSectionProps) {
  const [articles, setArticles] = useState<ArticleType[]>(initialData?.docs || []);
  const [filters, setFilters] = useState({
    limit: LATESTNEWS_RESULT_NEWS_LIMIT,
    page: 1,
  });

  const { data, isFetching } = useQuery({
    queryKey: ["fetchLatestNews", filters.page],
    enabled: filters.page > 1,
    queryFn: () => fetchLatestNews({ limit: filters?.limit, page: filters?.page, lang }),
  });

  const handleLoadMore = () => {
    setFilters(prev => ({ ...prev, page: prev.page + 1 }));
  };

  useEffect(() => {
    if (data) {
      setArticles(prev => [...prev, ...(data?.data?.docs || [])]);
    }
  }, [data]);

  const showLoadMore =
    articles?.length > 0 &&
    ((filters.page === 1 && initialData.totalDocs > articles.length) ||
      (data && data?.data?.totalDocs > articles.length));

  useEffect(() => {
    setArticles(initialData?.docs || []);
  }, [initialData]);

  return (
    <section id="latest-news-section">
      <div
        className="grid sm:grid-cols-2 lg:grid-cols-2 gap-x-8 gap-y-5 mt-4"
        role="list"
        aria-label="Latest news articles"
      >
        {articles?.map((article, index) => (
          <article key={index} role="listitem">
            <HorizontalArticleCard
              article={article}
              index={index}
              variant="md"
              showDate
              showCategory={false}
            />
          </article>
        ))}
      </div>

      {showLoadMore && (
        <div className="flex items-center justify-center mt-8">
          <button
            className="load-more-btn"
            onClick={handleLoadMore}
            disabled={isFetching}
            aria-label={getLabel(lang, "LOAD_MORE")}
          >
            {getLabel(lang, "LOAD_MORE")}
          </button>
        </div>
      )}
    </section>
  );
}
